/*=========================================================================================
  File Name: router.js
  Description: Routes for vue-router. Lazy loading is enabled.
  Object Strucutre:
                    path => router path
                    name => router name
                    component(lazy loading) => component to load
                    meta : {
                      rule => which user can have access (ACL)
                      breadcrumb => Add breadcrumb to specific page
                      pageTitle => Display title besides breadcrumb
                    }
  ----------------------------------------------------------------------------------------
  Item Name: Vuexy - Vuejs, HTML & Laravel Admin Dashboard Template
  Author: Pixinvent
  Author URL: http://www.themeforest.net/user/pixinvent
==========================================================================================*/
import {
  PERMISSION_NEW_MAIN_ENTITY,
  PERMISSION_OLD_MAIN_ENTITY
} from '@/acl/acl';

const authRoutes = [
  {
    // =============================================================================
    // MAIN LAYOUT ROUTES
    // =============================================================================
    path: "",
    component: () => import("../layouts/main/Main.vue"),
    children: [
      {
        path: "/",
        name: "dashboard",
        meta: {
          pageTitle: "Dashboard",
        },
        component: () => import("../views/DashboardAnalytics.vue"),
      },
      {
        path: '/companies',
        name: 'company.list',
        component: () => import('@/views/pages/companies/CompanyList.vue'),
        meta: {
          breadcrumb: [
            { title: 'Home', url: '/' },
            { title: 'Entities', url: '/companies' },
            { title: 'List', active: true }
          ],
          pageTitle: 'Company List',
        }
      },
      {
        path: '/companies/create',
        name: 'company.create',
        component: () => import('@/views/pages/companies/create/CompanyCreate.vue'),
        meta: {
          breadcrumb: [
            { title: 'Home', url: '/' },
            { title: 'Entities', url: '/companies' },
            { title: 'Create', active: true }
          ],
          pageTitle: 'Company Create',
        }
      },
      {
        path: '/companies/create/:contactId',
        name: 'company.create.from-contact',
        component: () => import('@/views/pages/companies/create/CompanyCreate.vue'),
        meta: {
          breadcrumb: [
            { title: 'Home', url: '/' },
            { title: 'Entities', url: '/companies' },
            { title: 'Create', active: true }
          ],
          pageTitle: 'Company Create',
        }
      },
      {
        path: '/companies/:id',
        name: 'company',
        component: () => import('@/views/pages/companies/view/Company.vue'),
        meta: {
          breadcrumb: [
            { title: 'Home', url: '/' },
            { title: 'Entities', url: '/companies' },
            { title: 'Details', active: true }
          ],
          pageTitle: 'Company View',
        },
        children:[
          {
            path: '/companies/:id/details',
            name: 'company.view',
            component: () => import('@/views/pages/companies/view/CompanyView.vue'),
            meta: {
              breadcrumb: [
                { title: 'Home', url: '/' },
                { title: 'Entities', url: '/companies' },
                { title: 'Details', active: true }
              ],
              pageTitle: 'Company Details',
            }
          },
          {
            path: '/companies/edit/:id',
            name: 'company.edit',
            component: () => import('@/views/pages/companies/edit/CompanyEdit.vue'),
            meta: {
              breadcrumb: [
                { title: 'Home', url: '/' },
                { title: 'Entities', url: '/companies' },
                { title: 'Edit', active: true }
              ],
              pageTitle: 'Company Edit',
            }
          },
          {
            path: '/companies/:id/addresses',
            name: 'company.addresses.list',
            component: () => import('@/views/pages/companies/addresses/CompanyViewAddresses.vue'),
            meta: {
              breadcrumb: [
                { title: 'Home', url: '/' },
                { title: 'Entities', url: '/companies' },
                { title: 'Addresses', active: true }
              ],
              pageTitle: 'Addresses',
            }
          },
          {
            path: '/companies/edit/:id/addresses/create',
            name: 'company.addresses.create',
            component: () => import('@/views/pages/companies/addresses/CompanyAddressCreate.vue'),
            meta: {
              breadcrumb: [
                { title: 'Home', url: '/' },
                { title: 'Entities', url: '/companies' },
                { title: 'Create Address', active: true }
              ],
              pageTitle: 'Create Address',
            }
          },
          {
            path: '/companies/edit/:id/addresses/:addressId',
            name: 'company.addresses.edit',
            component: () => import('@/views/pages/companies/addresses/CompanyAddressEdit.vue'),
            meta: {
              breadcrumb: [
                { title: 'Home', url: '/' },
                { title: 'Entities', url: '/companies' },
                { title: 'Edit Address', active: true }
              ],
              pageTitle: 'Edit Address',
            }
          },
          {
            path: '/companies/:id/tobas',
            name: 'company.tobas.list',
            component: () => import('@/views/pages/companies/tobas/CompanyTobas.vue'),
            meta: {
              breadcrumb: [
                { title: 'Home', url: '/' },
                { title: 'Entities', url: '/companies' },
                { title: 'View TOBAs', active: true }
              ],
              pageTitle: 'View TOBAs',
            }
          },
          {
            path: '/companies/create/:id/toba',
            name: 'company.tobas.create',
            component: () => import('@/views/pages/companies/tobas/CompanyTobaCreate.vue'),
            meta: {
              breadcrumb: [
                { title: 'Home', url: '/' },
                { title: 'Entities', url: '/companies' },
                { title: 'Create TOBA', active: true }
              ],
              pageTitle: 'Create TOBA',
            }
          },
          {
            path: '/companies/edit/:id/toba/:tobaId',
            name: 'company.tobas.edit',
            component: () => import('@/views/pages/companies/tobas/CompanyTobaEdit.vue'),
            meta: {
              breadcrumb: [
                { title: 'Home', url: '/' },
                { title: 'Entities', url: '/companies' },
                { title: 'Edit TOBA', active: true }
              ],
              pageTitle: 'Edit TOBA',
            }
          },
          {
            path: '/companies/:id/contacts',
            name: 'company.contacts.list',
            component: () => import('@/views/pages/companies/contacts/CompanyViewContacts.vue'),
            meta: {
              breadcrumb: [
                { title: 'Home', url: '/' },
                { title: 'Entities', url: '/companies' },
                { title: 'Contacts', active: true }
              ],
              pageTitle: 'Contacts',
            }
          },
          {
            path: '/companies/:id/distribution-list',
            name: 'company.distribution-list.list',
            component: () => import('@/views/pages/companies/DistributionList/CompanyDistributionListList.vue'),
            meta: {
              breadcrumb: [
                { title: 'Home', url: '/' },
                { title: 'Entities', url: '/companies' },
                { title: 'Distribution Lists', url: '/companies/:id/distribution-list', active: true }
              ],
              pageTitle: 'Distribution Lists',
            }
          },
          {
            path: '/companies/create/:id/distribution-list',
            name: 'company.distribution-list.create',
            component: () => import('@/views/pages/companies/DistributionList/DistributionListCreate.vue'),
            meta: {
              breadcrumb: [
                { title: 'Home', url: '/' },
                { title: 'Entities', url: '/companies' },
                { title: 'Distribution List', url: '/companies/:id/distribution-list' },
                { title: 'Create', active: true }
              ],
              pageTitle: 'Create Distribution List',
            }
          },
          {
            path: '/companies/:id/distribution-list/:distributionListId',
            name: 'company.distribution-list.view',
            component: () => import('@/views/pages/companies/DistributionList/DistributionListView.vue'),
            meta: {
              breadcrumb: [
                { title: 'Home', url: '/' },
                { title: 'Entities', url: '/companies' },
                { title: 'Distribution List', url: '/companies/:id/distribution-list' },
                { title: 'View', active: true }
              ],
              pageTitle: 'View Distribution List',
            }
          },
          {
            path: '/companies/edit/:id/distribution-list/:distributionListId',
            name: 'company.distribution-list.edit',
            component: () => import('@/views/pages/companies/DistributionList/DistributionListEdit.vue'),
            meta: {
              breadcrumb: [
                { title: 'Home', url: '/' },
                { title: 'Entities', url: '/companies' },
                { title: 'Distribution List', url: '/companies/:id/distribution-list' },
                { title: 'Edit', active: true }
              ],
              pageTitle: 'Edit Distribution List',
            }
          },
          {
            path: '/companies/:id/commercial-products',
            name: 'company.commercial-products.view',
            component: () => import('@/views/pages/companies/commercial-products/Fuw.vue'),
            meta: {
              breadcrumb: [
                { title: 'Home', url: '/' },
                { title: 'Entities', url: '/companies' },
                { title: 'Commercial Products', active: true }
              ],
              pageTitle: 'Commercial Products',
            },
          },
        ],
      },
      {
        path: '/contacts',
        name: 'contact.list',
        component: () => import('@/views/pages/user/user-list/UserList.vue'),
        meta: {
          breadcrumb: [
            { title: 'Home', url: '/' },
            { title: 'Contacts', url: '/contacts'},
            { title: 'List', active: true }
          ],
          pageTitle: 'User List',
        }
      },
      {
        path: '/contacts/create',
        name: 'contact.create',
        component: () => import('@/views/pages/user/user-create/UserCreate.vue'),
        meta: {
          breadcrumb: [
            { title: 'Home', url: '/' },
            { title: 'Contacts', url: '/contacts'},
            { title: 'Create', active: true }
          ],
          pageTitle: 'User Create',
        }
      },
      {
        path: '/contacts/:id',
        name: 'contact',
        component: () => import('@/views/pages/user/user-view/User.vue'),
        meta: {
          breadcrumb: [
            { title: 'Home', url: '/' },
            { title: 'Contacts', url: '/contacts'},
            { title: 'View', active: true }
          ],
          pageTitle: 'User Edit',
        },
        children:[
          {
            path: '/contacts/:id/details',
            name: 'contact.view',
            component: () => import('@/views/pages/user/user-view/UserView.vue'),
            meta: {
              breadcrumb: [
                { title: 'Home', url: '/' },
                { title: 'Contacts', url: '/contacts'},
                { title: 'View', active: true }
              ],
              pageTitle: 'User View',
            }
          },
          {
            path: '/contacts/edit/:id',
            name: 'contact.edit',
            component: () => import('@/views/pages/user/user-edit/UserEdit.vue'),
            meta: {
              breadcrumb: [
                { title: 'Home', url: '/' },
                { title: 'Contacts', url: '/contacts'},
                { title: 'Edit', active: true }
              ],
              pageTitle: 'User Edit',
            }
          },
          {
            path: '/contacts/edit/:id/addresses',
            name: 'contact.addresses.list',
            component: () => import('@/views/pages/user/addresses/UserViewAddresses.vue'),
            meta: {
              breadcrumb: [
                { title: 'Home', url: '/' },
                { title: 'Contacts', url: '/companies' },
                { title: 'Addresses', active: true }
              ],
              pageTitle: 'Addresses',
            }
          },
          {
            path: '/contacts/edit/:id/addresses/create',
            name: 'contact.addresses.create',
            component: () => import('@/views/pages/user/addresses/UserAddressCreate.vue'),
            meta: {
              breadcrumb: [
                { title: 'Home', url: '/' },
                { title: 'Contacts', url: '/contacts'},
                { title: 'Create Address', active: true }
              ],
              pageTitle: 'Address Create',
            }
          },
          {
            path: '/contacts/edit/:id/addresses/:addressId',
            name: 'contact.addresses.edit',
            component: () => import('@/views/pages/user/addresses/UserAddressEdit.vue'),
            meta: {
              breadcrumb: [
                { title: 'Home', url: '/' },
                { title: 'Contacts', url: '/contacts'},
                { title: 'Edit Address', active: true }
              ],
              pageTitle: 'Address Edit',
            }
          },
          {
            path: '/contacts/:id/beneficiaries',
            name: 'contact.beneficiaries.view',
            component: () => import('@/views/pages/user/beneficiaries/BeneficiaryView.vue'),
            meta: {
              breadcrumb: [
                { title: 'Home', url: '/' },
                { title: 'Contacts', url: '/contacts'},
                { title: 'Beneficiaries', active: true }
              ],
              pageTitle: 'Beneficiary Details',
            }
          },
          {
            path: '/contacts/:id/beneficiaries/edit',
            name: 'contact.beneficiaries.edit',
            component: () => import('@/views/pages/user/beneficiaries/BeneficiaryEdit.vue'),
            meta: {
              breadcrumb: [
                { title: 'Home', url: '/' },
                { title: 'Contacts', url: '/contacts'},
                { title: 'Beneficiaries', url: '/contacts/:id/beneficiaries'},
                { title: 'Edit', active: true }
              ],
              pageTitle: 'Edit Beneficiary',
            }
          },
          {
            path: '/contacts/:id/distribution-list',
            name: 'contact.distribution-list.list',
            component: () => import('@/views/pages/user/user-view/UserViewDistributionList.vue'),
            meta: {
              breadcrumb: [
                { title: 'Home', url: '/' },
                { title: 'Contacts', url: '/contacts'},
                { title: 'Distribution Lists', active: true }
              ],
              pageTitle: 'Distribution Lists',
            }
          },
        ]
      },
      {
        path: "/roles",
        name: "role.list",
        component: () => import("@/views/pages/role/RoleList.vue"),
        meta: {
          breadcrumb: [
            {title: "Home", url: "/"},
            {title: "Roles", url: '/roles'},
            {title: "List", active: true}
          ],
          pageTitle: "Role List",
        }
      },
      {
        path: "/roles/create",
        name: "role.create",
        component: () => import("@/views/pages/role/create/RoleCreate.vue"),
        meta: {
          breadcrumb: [
            {title: "Home", url: "/"},
            {title: "Roles", url: '/roles'},
            {title: "Create", active: true}
          ],
          pageTitle: "Role Create",
        }
      },
      {
        path: "/roles/edit/:id",
        name: "role.edit",
        component: () => import("@/views/pages/role/edit/RoleEdit.vue"),
        meta: {
          breadcrumb: [
            {title: "Home", url: "/"},
            {title: "Role"},
            {title: "Edit", active: true}
          ],
          pageTitle: "Role Edit",
        }
      },
      {
        path: "/roles/:id",
        name: "role.view",
        component: () => import("@/views/pages/role/view/RoleView.vue"),
        meta: {
          breadcrumb: [
            {title: "Home", url: "/"},
            {title: "Roles", url: '/roles'},
            {title: "View", active: true}
          ],
          pageTitle: "Role View",
        }
      },
      {
        path: "/content-management/administrators",
        name: "content-management.administrators.list",
        component: () => import("@/views/pages/content-management/administrators/List.vue"),
        meta: {
          breadcrumb: [
            {title: "Home", url: "/"},
            {title: "Administrators", url: '/content-management/administrators', active: true},
          ],
          pageTitle: "Administrators",
        }
      },
      {
        path: "/content-management/administrators/create",
        name: "content-management.administrators.create",
        component: () => import("@/views/pages/content-management/administrators/Create.vue"),
        meta: {
          breadcrumb: [
            {title: "Home", url: "/"},
            {title: "Administrators", url: '/content-management/administrators'},
            {title: "Create", active: true}
          ],
          pageTitle: "Create Administrator",
        }
      },
      {
        path: "/content-management/administrators/:id",
        name: "content-management.administrators.edit",
        component: () => import("@/views/pages/content-management/administrators/Edit.vue"),
        meta: {
          breadcrumb: [
            {title: "Home", url: "/"},
            {title: "Administrators", url: '/content-management/administrators'},
            {title: "Edit", active: true}
          ],
          pageTitle: "Edit Administrator",
        }
      },
      {
        path: "/content-management/news/",
        name: "content-management.news.list",
        component: () => import("@/views/pages/content-management/news/List.vue"),
        meta: {
          breadcrumb: [
            {title: "Home", url: "/"},
            {title: "News", url: '/content-management/news', active: true}
          ],
          pageTitle: "News",
        }
      },
      {
          path: "/content-management/news/create",
          name: "content-management.news.create",
          component: () => import("@/views/pages/content-management/news/Create.vue"),
          meta: {
            breadcrumb: [
              {title: "Home", url: "/"},
              {title: "News", url: '/content-management/news'},
              {title: "Create", active: true}
            ],
            pageTitle: "News Create",
          }
      },
      {
          path: "/content-management/news/:id",
          name: "content-management.news.edit",
          component: () => import("@/views/pages/content-management/news/Edit.vue"),
          meta: {
            breadcrumb: [
              {title: "Home", url: "/"},
              {title: "News", url: '/content-management/news'},
              {title: "Edit", active: true}
            ],
            pageTitle: "News Edit",
          }
      },
      {
        path: "/content-management/nodes/",
        name: "content-management.nodes.list",
        component: () => import("@/views/pages/content-management/nodes/List.vue"),
        meta: {
          breadcrumb: [
            {title: "Home", url: "/"},
            {title: "Nodes", url: '/content-management/nodes', active: true}
          ],
          pageTitle: "Nodes",
        }
      },
      {
          path: "/content-management/nodes/create",
          name: "content-management.nodes.create",
          component: () => import("@/views/pages/content-management/nodes/Create.vue"),
          meta: {
            breadcrumb: [
              {title: "Home", url: "/"},
              {title: "Nodes", url: '/content-management/nodes'},
              {title: "Create", active: true}
            ],
            pageTitle: "Nodes Create",
          }
      },
      {
          path: "/content-management/nodes/:id",
          name: "content-management.nodes.edit",
          component: () => import("@/views/pages/content-management/nodes/Edit.vue"),
          meta: {
            breadcrumb: [
              {title: "Home", url: "/"},
              {title: "Nodes", url: '/content-management/nodes'},
              {title: "Edit", active: true}
            ],
            pageTitle: "Nodes Edit",
          }
      },
      {
        path: "/personas/orion",
        name: "personas.orion.list",
        component: () => import("@/views/pages/personas/orion/PersonaList.vue"),
        meta: {
          breadcrumb: [
            {title: "Home", url: "/"},
            {title: "Personas", url: '/personas'},
            {title: "Orion", url: '/personas/orion'},
            {title: "List", active: true}
          ],
          pageTitle: "Persona List",
        }
      },
      {
        path: "/personas/orion/create",
        name: "personas.orion.create",
        component: () => import("@/views/pages/personas/orion/PersonaCreate.vue"),
        meta: {
          breadcrumb: [
            {title: "Home", url: "/"},
            {title: "Personas", url: '/personas'},
            {title: "Orion", url: '/personas/orion'},
            {title: "Create", active: true}
          ],
          pageTitle: "Create Persona",
        }
      },
      {
        path: "/personas/orion/:id/edit",
        name: "personas.orion.edit",
        component: () => import("@/views/pages/personas/orion/PersonaEdit.vue"),
        meta: {
          breadcrumb: [
            {title: "Home", url: "/"},
            {title: "Personas", url: '/personas'},
            {title: "Orion", url: '/personas/orion'},
            {title: "Edit", active: true}
          ],
          pageTitle: "Persona Details",
        }
      },
      {
        path: "/personas/data-centre",
        name: "personas.data-centre.list",
        component: () => import("@/views/pages/personas/data-centre/PersonaList.vue"),
        meta: {
          breadcrumb: [
            {title: "Home", url: "/"},
            {title: "Personas", url: '/personas'},
            {title: "Data Centre", url: '/personas/data-centre'},
            {title: "List", active: true}
          ],
          pageTitle: "Persona List",
        }
      },
      {
        path: "/personas/data-centre/create",
        name: "personas.data-centre.create",
        component: () => import("@/views/pages/personas/data-centre/PersonaCreate.vue"),
        meta: {
          breadcrumb: [
            {title: "Home", url: "/"},
            {title: "Personas", url: '/personas'},
            {title: "Data Centre", url: '/personas/data-centre'},
            {title: "Create", active: true}
          ],
          pageTitle: "Create Persona",
        }
      },
      {
        path: "/personas/data-centre/:id/edit",
        name: "personas.data-centre.edit",
        component: () => import("@/views/pages/personas/data-centre/PersonaEdit.vue"),
        meta: {
          breadcrumb: [
            {title: "Home", url: "/"},
            {title: "Personas", url: '/personas'},
            {title: "Data Centre", url: '/personas/data-centre'},
            {title: "Edit", active: true}
          ],
          pageTitle: "Persona Details",
        }
      },
      {
        path: "/personas/landing-page",
        name: "personas.landing-page.list",
        component: () => import("@/views/pages/personas/landing-page/PersonaList.vue"),
        meta: {
          breadcrumb: [
            {title: "Home", url: "/"},
            {title: "Personas", url: '/personas'},
            {title: "Landing Page", url: '/personas/landing-page'},
            {title: "List", active: true}
          ],
          pageTitle: "Persona List",
        }
      },
      {
        path: "/personas/landing-page/create",
        name: "personas.landing-page.create",
        component: () => import("@/views/pages/personas/landing-page/PersonaCreate.vue"),
        meta: {
          breadcrumb: [
            {title: "Home", url: "/"},
            {title: "Personas", url: '/personas'},
            {title: "Landing Page", url: '/personas/landing-page'},
            {title: "Create", active: true}
          ],
          pageTitle: "Create Persona",
        }
      },
      {
        path: "/personas/landing-page/:id/edit",
        name: "personas.landing-page.edit",
        component: () => import("@/views/pages/personas/landing-page/PersonaEdit.vue"),
        meta: {
          breadcrumb: [
            {title: "Home", url: "/"},
            {title: "Personas", url: '/personas'},
            {title: "Landing Page", url: '/personas/landing-page'},
            {title: "Edit", active: true}
          ],
          pageTitle: "Persona Details",
        }
      },
      {
        path: "/personas/fulweb",
        name: "personas.fulweb.list",
        component: () => import("@/views/pages/personas/fulweb/PersonaList.vue"),
        meta: {
          breadcrumb: [
            {title: "Home", url: "/"},
            {title: "Personas", url: '/personas'},
            {title: "FulWeb", active: true}
          ],
          pageTitle: "Persona List",
        }
      },
      {
        path: "/personas/fulweb/create",
        name: "personas.fulweb.create",
        component: () => import("@/views/pages/personas/fulweb/PersonaCreate.vue"),
        meta: {
          breadcrumb: [
            {title: "Home", url: "/"},
            {title: "Personas", url: '/personas'},
            {title: "Fulweb", url: '/personas/fulweb'},
            {title: "Create", active: true}
          ],
          pageTitle: "Create Persona",
        }
      },
      {
        path: "/personas/fulweb/:id/edit",
        name: "personas.fulweb.edit",
        component: () => import("@/views/pages/personas/fulweb/PersonaEdit.vue"),
        meta: {
          breadcrumb: [
            {title: "Home", url: "/"},
            {title: "Personas", url: '/personas'},
            {title: "Fulweb", url: '/personas/fulweb'},
            {title: "Edit", active: true}
          ],
          pageTitle: "Persona Details",
        }
      },
      {
        path: "/personas/learn-dash",
        name: "personas.learn-dash.list",
        component: () => import("@/views/pages/personas/learn-dash/PersonaList.vue"),
        meta: {
          breadcrumb: [
            {title: "Home", url: "/"},
            {title: "Personas", url: '/personas'},
            {title: "Learn Dash", active: true}
          ],
          pageTitle: "Persona List",
        }
      },
      {
        path: "/personas/learn-dash/create",
        name: "personas.learn-dash.create",
        component: () => import("@/views/pages/personas/learn-dash/PersonaCreate.vue"),
        meta: {
          breadcrumb: [
            {title: "Home", url: "/"},
            {title: "Personas", url: '/personas'},
            {title: "Learn Dash", url: '/personas/learn-dash'},
            {title: "Create", active: true}
          ],
          pageTitle: "Create Persona",
        }
      },
      {
        path: "/personas/learn-dash/:id/edit",
        name: "personas.learn-dash.edit",
        component: () => import("@/views/pages/personas/learn-dash/PersonaEdit.vue"),
        meta: {
          breadcrumb: [
            {title: "Home", url: "/"},
            {title: "Personas", url: '/personas'},
            {title: "Learn Dash", url: '/personas/learn-dash'},
            {title: "Edit", active: true}
          ],
          pageTitle: "Persona Details",
        }
      },
      {
        path: "/personas/ipr-platform",
        name: "personas.ipr-platform.list",
        component: () => import("@/views/pages/personas/ipr-platform/PersonaList.vue"),
        meta: {
          breadcrumb: [
            {title: "Home", url: "/"},
            {title: "Personas", url: '/personas'},
            {title: "IPR Platform", active: true}
          ],
          pageTitle: "Persona List",
        }
      },
      {
        path: "/personas/ipr-platform/create",
        name: "personas.ipr-platform.create",
        component: () => import("@/views/pages/personas/ipr-platform/PersonaCreate.vue"),
        meta: {
          breadcrumb: [
            {title: "Home", url: "/"},
            {title: "Personas", url: '/personas'},
            {title: "IPR Platform", url: '/personas/ipr-platform'},
            {title: "Create", active: true}
          ],
          pageTitle: "Create Persona",
        }
      },
      {
        path: "/personas/ipr-platform/:id/edit",
        name: "personas.ipr-platform.edit",
        component: () => import("@/views/pages/personas/ipr-platform/PersonaEdit.vue"),
        meta: {
          breadcrumb: [
            {title: "Home", url: "/"},
            {title: "Personas", url: '/personas'},
            {title: "IPR Platform", url: '/personas/ipr-platform'},
            {title: "Edit", active: true}
          ],
          pageTitle: "Persona Details",
        }
      },
      {
        path: "/applications",
        name: "application.list",
        component: () => import("@/views/pages/applications/ApplicationList.vue"),
        meta: {
          breadcrumb: [
            {title: "Home", url: "/"},
            {title: "Applications", url: '/applications'},
            {title: "List", active: true}
          ],
          pageTitle: "Application List",
        }
      },
      {
        path: "/applications/create",
        name: "application.create",
        component: () => import("@/views/pages/applications/create/ApplicationCreate.vue"),
        meta: {
          breadcrumb: [
            {title: "Home", url: "/"},
            {title: "Applications", url: '/applications'},
            {title: "Create", active: true}
          ],
          pageTitle: "Application Create",
        }
      },
      {
        path: "/applications/edit/:id",
        name: "application.edit",
        component: () => import("@/views/pages/applications/edit/ApplicationEdit.vue"),
        meta: {
          breadcrumb: [
            {title: "Home", url: "/"},
            {title: "Applications", url: '/applications'},
            {title: "Edit", active: true}
          ],
          pageTitle: "Application Edit",
        }
      },
      {
        path: "/applications/:id",
        name: "application.view",
        component: () => import("@/views/pages/applications/view/ApplicationView.vue"),
        meta: {
          breadcrumb: [
            {title: "Home", url: "/"},
            {title: "Applications", url: '/applications'},
            {title: "View", active: true}
          ],
          pageTitle: "Application View",
        }
      },
      {
        path: "/auth0-logs",
        name: "log.list",
        component: () => import("@/views/pages/log/LogList.vue"),
        meta: {
          breadcrumb: [
            {title: "Home", url: "/"},
            {title: "Auth0 Logs", url: '/auth0-logs'},
            {title: "List", active: true}
          ],
          pageTitle: "Auth0 Logs",
        }
      },
      {
        path: "/auth0-logs/:id",
        name: "log.view",
        component: () => import("@/views/pages/log/view/LogView.vue"),
        meta: {
          breadcrumb: [
            {title: "Home", url: "/"},
            {title: "Auth0 Logs", url: '/auth0-logs'},
            {title: "View", active: true}
          ],
          pageTitle: "Auth0 Log",
        }
      },
      {
        path: "/audits",
        name: "audit.list",
        component: () => import("@/views/pages/audit/AuditList.vue"),
        meta: {
          breadcrumb: [
            {title: "Home", url: "/"},
            {title: "Audits", url: '/audits'},
            {title: "List", active: true}
          ],
          pageTitle: "Audits",
        }
      },
      {
        path: "/audits/:id",
        name: "audit.view",
        component: () => import("@/views/pages/audit/view/AuditView.vue"),
        meta: {
          breadcrumb: [
            {title: "Home", url: "/"},
            {title: "Audits", url: '/audits'},
            {title: "View", active: true}
          ],
          pageTitle: "Audit",
        }
      },
      {
        path: "/usersync",
        name: "sync.user.list",
        component: () => import("@/views/pages/sync/SyncUserList.vue"),
        meta: {
          breadcrumb: [
            {title: "Home", url: "/"},
            {title: "User Sync", url: '/usersync'},
            {title: "List", active: true}
          ],
          pageTitle: "User Sync",
        }
      },
      {
        path: "/rolesync",
        name: "sync.role.list",
        component: () => import("@/views/pages/sync/SyncRoleList.vue"),
        meta: {
          breadcrumb: [
            {title: "Home", url: "/"},
            {title: "Role Sync", url: '/rolesync'},
            {title: "List", active: true}
          ],
          pageTitle: "Role Sync",
        }
      },
      {
        path: "/risk-codes",
        name: "risk-codes.list",
        component: () => import("@/views/pages/risk-codes/RiskCodeList.vue"),
        meta: {
          breadcrumb: [
            {title: "Home", url: "/"},
            {title: "Risk Codes", url: '/risk-codes'},
            {title: "List", active: true}
          ],
          pageTitle: "Risk Codes",
        }
      },
      {
        path: "/risk-codes/create",
        name: "risk-codes.create",
        component: () => import("@/views/pages/risk-codes/RiskCodeCreate.vue"),
        meta: {
          breadcrumb: [
            {title: "Home", url: "/"},
            {title: "Risk Codes", url: '/risk-codes'},
            {title: "Create", active: true}
          ],
          pageTitle: "Risk Codes",
        }
      },
      {
        path: "/risk-codes/:id/view",
        name: "risk-codes.view",
        component: () => import("@/views/pages/risk-codes/RiskCodeView.vue"),
        meta: {
          breadcrumb: [
            {title: "Home", url: "/"},
            {title: "Risk Codes", url: '/risk-codes'},
            {title: "View", active: true}
          ],
          pageTitle: "Risk Codes",
        }
      },
      {
        path: "/risk-codes/:id/edit",
        name: "risk-codes.edit",
        component: () => import("@/views/pages/risk-codes/RiskCodeEdit.vue"),
        meta: {
          breadcrumb: [
            {title: "Home", url: "/"},
            {title: "Risk Codes", url: '/risk-codes'},
            {title: "Edit", active: true}
          ],
          pageTitle: "Risk Codes",
        }
      },
      {
        path: "/products",
        name: "products.list",
        component: () => import("@/views/pages/products/ProductList.vue"),
        meta: {
          breadcrumb: [
            {title: "Home", url: "/"},
            {title: "Products", url: '/products'},
            {title: "List", active: true}
          ],
          pageTitle: "Product List",
        }
      },
      {
        path: "/products/create",
        name: "products.create",
        component: () => import("@/views/pages/products/ProductCreate.vue"),
        meta: {
          breadcrumb: [
            {title: "Home", url: "/"},
            {title: "Products", url: '/products'},
            {title: "Create", active: true}
          ],
          pageTitle: "Create Product",
        }
      },
      {
        path: "/products/:id",
        name: "products.view",
        component: () => import("@/views/pages/products/ProductDetails.vue"),
        meta: {
          breadcrumb: [
            {title: "Home", url: "/"},
            {title: "Products", url: '/products'},
            {title: "Details", active: true}
          ],
          pageTitle: "Product Details",
        }
      },
      {
        path: "/products/:id/edit",
        name: "products.edit",
        component: () => import("@/views/pages/products/ProductEdit.vue"),
        meta: {
          breadcrumb: [
            {title: "Home", url: "/"},
            {title: "Products", url: '/products'},
            {title: "Edit", active: true}
          ],
          pageTitle: "Product Details",
        }
      },
      {
        path: "/commercial-products",
        name: "commercial-products.list",
        component: () => import("@/views/pages/commercial-products/CommercialProductList.vue"),
        meta: {
          breadcrumb: [
            {title: "Home", url: "/"},
            {title: "Commercial Products", url: '/commercial-products'},
            {title: "List", active: true}
          ],
          pageTitle: "Commercial Products List",
        }
      },
      {
        path: "/commercial-products/create",
        name: "commercial-products.create",
        component: () => import("@/views/pages/commercial-products/CommercialProductCreate.vue"),
        meta: {
          breadcrumb: [
            {title: "Home", url: "/"},
            {title: "Commercial Products", url: '/commercial-products'},
            {title: "Create", active: true}
          ],
          pageTitle: "Create Commercial Product",
        }
      },
      {
        path: "/commercial-products/:id",
        name: "commercial-products",
        component: () => import("@/views/pages/commercial-products/CommercialProduct.vue"),
        meta: {
          breadcrumb: [
            {title: "Home", url: "/"},
            {title: "Commercial Products", url: '/commercial-products'},
            {title: "View", active: true}
          ],
          pageTitle: "Commercial Product",
        },
        children: [
          {
            path: "/commercial-products/:id/details",
            name: "commercial-products.view",
            component: () => import("@/views/pages/commercial-products/CommercialProductDetails.vue"),
            meta: {
              breadcrumb: [
                {title: "Home", url: "/"},
                {title: "Commercial Products", url: '/commercial-products'},
                {title: "Details", active: true}
              ],
              pageTitle: "Commercial Product Details",
            }
          },
          {
            path: "/commercial-products/:id/producers",
            name: "commercial-products.producers.view",
            component: () => import("@/views/pages/commercial-products/CommercialProductProducerList.vue"),
            meta: {
              breadcrumb: [
                {title: "Home", url: "/"},
                {title: "Commercial Products", url: '/commercial-products'},
                {title: "Producers", active: true}
              ],
              pageTitle: "Assigned Producers",
            }
          },
          {
            path: "/commercial-products/:id/alternative-commercial-products",
            name: "commercial-products.alternative-commercial-products.view",
            component: () => import("@/views/pages/commercial-products/AlternativeCommercialProductsList.vue"),
            meta: {
              breadcrumb: [
                {title: "Home", url: "/"},
                {title: "Commercial Products", url: '/commercial-products'},
                {title: "Producers", active: true}
              ],
              pageTitle: "Alternative Commercial Products",
            }
          },
          {
            path: "/commercial-products/:id/edit",
            name: "commercial-products.edit",
            component: () => import("@/views/pages/commercial-products/CommercialProductEdit.vue"),
            meta: {
              breadcrumb: [
                {title: "Home", url: "/"},
                {title: "Commercial Products", url: '/commercial-products'},
                {title: "Edit", active: true}
              ],
              pageTitle: "Commercial Product Edit",
            }
          },
          {
            path: "/commercial-products/:id/extra-info",
            name: "commercial-products.extra-info.view",
            component: () => import("@/views/pages/commercial-products/CommercialProductExtraInfo.vue"),
            meta: {
              breadcrumb: [
                {title: "Home", url: "/"},
                {title: "Commercial Products", url: '/commercial-products'},
                {title: "Extra Info", active: true}
              ],
              pageTitle: "Commercial Product Extra Info",
            }
          },
          {
            path: "/commercial-products/:id/extra-info-edit",
            name: "commercial-products.extra-info.edit",
            component: () => import("@/views/pages/commercial-products/CommercialProductExtraInfoEdit.vue"),
            meta: {
              breadcrumb: [
                {title: "Home", url: "/"},
                {title: "Commercial Products", url: '/commercial-products'},
                {title: "Extra Info", active: true}
              ],
              pageTitle: "Commercial Product Extra Info",
            }
          },
        ]
      },
      {
        path: "/mapping-renewals",
        name: "mapping-renewals.list",
        component: () => import("@/views/pages/mapping-renewals/MappingRenewalList.vue"),
        meta: {
          breadcrumb: [
            {title: "Home", url: "/"},
            {title: "Mapping Renewal", url: '/mapping-renewals'},
            {title: "List", active: true}
          ],
          pageTitle: "Mapping Renewal List",
        }
      },
      {
        path: "/mapping-renewals/create",
        name: "mapping-renewals.create",
        component: () => import("@/views/pages/mapping-renewals/MappingRenewalCreate.vue"),
        meta: {
          breadcrumb: [
            {title: "Home", url: "/"},
            {title: "Mapping Renewal", url: '/mapping-renewals'},
            {title: "Create", active: true}
          ],
          pageTitle: "Create Mapping Renewal",
        }
      },
      {
        path: "/mapping-renewals/:id",
        name: "mapping-renewals.view",
        component: () => import("@/views/pages/mapping-renewals/MappingRenewalDetails.vue"),
        meta: {
          breadcrumb: [
            {title: "Home", url: "/"},
            {title: "Mapping Renewal", url: '/mapping-renewals'},
            {title: "Details", active: true}
          ],
          pageTitle: "Mapping Renewal Details",
        }
      },
      {
        path: "/mapping-renewals/:id/edit",
        name: "mapping-renewals.edit",
        component: () => import("@/views/pages/mapping-renewals/MappingRenewalEdit.vue"),
        meta: {
          breadcrumb: [
            {title: "Home", url: "/"},
            {title: "Mapping Renewal", url: '/mapping-renewals'},
            {title: "Edit", active: true}
          ],
          pageTitle: "Mapping Renewal Details",
        }
      },
      {
        path: "/claims-notifications/commercial-claims",
        name: "commercial-claims.list",
        component: () => import("@/views/pages/commercial-claims/CommercialClaimList.vue"),
        meta: {
          breadcrumb: [
            {title: "Home", url: "/"},
            {title: "Claims Notifications"},
            {title: "Commercial Claims", url: '/commercial-claims'},
            {title: "List", active: true}
          ],
          pageTitle: "Commercial Claims List",
        }
      },
      {
        path: "/claims-notifications/commercial-claims/create",
        name: "commercial-claims.create",
        component: () => import("@/views/pages/commercial-claims/CommercialClaimCreate.vue"),
        meta: {
          breadcrumb: [
            {title: "Home", url: "/"},
            {title: "Claims Notifications"},
            {title: "Commercial Claims", url: '/commercial-claims'},
            {title: "Create", active: true}
          ],
          pageTitle: "Create Commercial Claim",
        }
      },
      {
        path: "/claims-notifications/commercial-claims/:id",
        name: "commercial-claims",
        component: () => import("@/views/pages/commercial-claims/CommercialClaim.vue"),
        meta: {
          breadcrumb: [
            {title: "Home", url: "/"},
            {title: "Claims Notifications"},
            {title: "Commercial Claims", url: '/commercial-claims'},
            {title: "View", active: true}
          ],
          pageTitle: "Commercial Claim",
        },
        children: [
          {
            path: "/claims-notifications/commercial-claims/:id/details",
            name: "commercial-claims.view",
            component: () => import("@/views/pages/commercial-claims/CommercialClaimDetails.vue"),
            meta: {
              breadcrumb: [
                {title: "Home", url: "/"},
                {title: "Claims Notifications"},
                {title: "Commercial Claims", url: '/commercial-claims'},
                {title: "Details", active: true}
              ],
              pageTitle: "Commercial Claim Details",
            }
          },
          {
            path: "/claims-notifications/commercial-claims/:id/edit",
            name: "commercial-claims.edit",
            component: () => import("@/views/pages/commercial-claims/CommercialClaimEdit.vue"),
            meta: {
              breadcrumb: [
                {title: "Home", url: "/"},
                {title: "Claims Notifications"},
                {title: "Commercial Claims", url: '/commercial-claims'},
                {title: "Edit", active: true}
              ],
              pageTitle: "Commercial Claim Edit",
            }
          },
        ]
      },
      {
        path: "/claims-documents/:typeId",
        name: "claims-dynamic.list",
        component: () => import("@/views/pages/claims-documents/dynamic/DynamicList.vue"),
        meta: {
          breadcrumb: [
            {title: "Home", url: "/"},
            {title: "Claims Documents"},
            {title: "Dynamic", url: '/claims-documents/:typeId', dynamic: true},
            {title: "List", active: true}
          ],
          pageTitle: "Dynamic List",
        }
      },
      {
        path: "/claims-documents/:typeId/create",
        name: "claims-dynamic.create",
        component: () => import("@/views/pages/claims-documents/dynamic/DynamicCreate.vue"),
        meta: {
          breadcrumb: [
            {title: "Home", url: "/"},
            {title: "Claims Documents"},
            { title: "Dynamic", url: '/claims-documents/:typeId', dynamic: true},
            {title: "Create", active: true}
          ],
          pageTitle: "Upload Dynamic Document",
        }
      },
      {
        path: "/claims-documents/:typeId/:id/edit",
        name: "claims-dynamic.edit",
        component: () => import("@/views/pages/claims-documents/dynamic/DynamicEdit.vue"),
        meta: {
          breadcrumb: [
            {title: "Home", url: "/"},
            {title: "Claims Documents"},
            {title: "Dynamic", url: '/claims-documents/:typeId', dynamic: true},
            {title: "Edit", active: true}
          ],
          pageTitle: "Edit Dynamic Document",
        }
      },
      {
        path: "/product-bundles",
        name: "product-bundles.list",
        component: () => import("@/views/pages/product-bundles/ProductBundleList.vue"),
        meta: {
          breadcrumb: [
            {title: "Home", url: "/"},
            {title: "Product Bundles", url: '/product-bundles'},
            {title: "List", active: true}
          ],
          pageTitle: "Product Bundle List",
        }
      },
      {
        path: "/product-bundles/create",
        name: "product-bundles.create",
        component: () => import("@/views/pages/product-bundles/ProductBundleCreate.vue"),
        meta: {
          breadcrumb: [
            {title: "Home", url: "/"},
            {title: "Product Bundles", url: '/product-bundles'},
            {title: "Create", active: true}
          ],
          pageTitle: "Create Product Bundle",
        }
      },
      {
        path: "/product-bundles/:id",
        name: "product-bundles.view",
        component: () => import("@/views/pages/product-bundles/ProductBundleDetails.vue"),
        meta: {
          breadcrumb: [
            {title: "Home", url: "/"},
            {title: "Product Bundles", url: '/product-bundles'},
            {title: "Details", active: true}
          ],
          pageTitle: "Product Bundle Details",
        }
      },
      {
        path: "/product-bundles/:id/edit",
        name: "product-bundles.edit",
        component: () => import("@/views/pages/product-bundles/ProductBundleEdit.vue"),
        meta: {
          breadcrumb: [
            {title: "Home", url: "/"},
            {title: "Product Bundles", url: '/product-bundles'},
            {title: "Edit", active: true}
          ],
          pageTitle: "Edit Product Bundle",
        }
      },
      {
        path: "/client-maps",
        name: "client-map.list",
        component: () => import("@/views/pages/client-map/ClientMapList.vue"),
        meta: {
          breadcrumb: [
            {title: "Home", url: "/"},
            {title: "Client Maps", url: '/client-maps'},
            {title: "List", active: true}
          ],
          pageTitle: "Client Maps",
        }
      },
      {
        path: "/client-maps/create",
        name: "client-map.create",
        component: () => import("@/views/pages/client-map/ClientMapCreate.vue"),
        meta: {
          breadcrumb: [
            {title: "Home", url: "/"},
            {title: "Client Maps", url: '/client-maps'},
            {title: "Create", active: true}
          ],
          pageTitle: "Client Maps",
        }
      },
      {
        path: "/facilities",
        name: "facilities.list",
        component: () => import("@/views/pages/facilities/FacilityList.vue"),
        meta: {
          breadcrumb: [
            {title: "Home", url: "/"},
            {title: "Facilities", url: '/facilities'},
            {title: "List", active: true},

        ],
        pageTitle: "Facility List",
      }
    },
    {
      path: "/facilities/create",
      name: "facilities.create",
      component: () => import("@/views/pages/facilities/FacilityCreate.vue"),
      meta: {
        breadcrumb: [
          {title: "Home", url: "/"},
          {title: "Facilities", url: '/facilities'},
          {title: "Create", active: true}
        ],
        pageTitle: "Create Facility",
      }
    },
    {
      path: "/facilities/:id",
      name: "facilities",
      component: () => import("@/views/pages/facilities/Facility.vue"),
      meta: {
        breadcrumb: [
          {title: "Home", url: "/"},
          {title: "Facilities", url: '/facilities'},
          {title: "Details", active: true}
        ],
        pageTitle: "Facility Details",
      },
      children: [
        {
          path: "/facilities/:id/details",
          name: "facilities.view",
          component: () => import("@/views/pages/facilities/FacilityView.vue"),
          meta: {
            breadcrumb: [
              {title: "Home", url: "/"},
              {title: "Facilities", url: '/facilities'},
              {title: "Details", active: true}
            ],
            pageTitle: "Facility Details",
          }
        },
        {
          path: "/facilities/:id/sections",
          name: "facilities.sections.list",
          component: () => import("@/views/pages/facilities/sections/SectionList.vue"),
          meta: {
            breadcrumb: [
              {title: "Home", url: "/"},
              {title: "Facilities", url: '/facilities'},
              {title: "Sections", active: true}
            ],
            pageTitle: "Facility Sections",
          }
        },
        {
          path: "/facilities/:id/sections/create",
          name: "facilities.sections.create",
          component: () => import("@/views/pages/facilities/sections/SectionCreate.vue"),
          meta: {
            breadcrumb: [
              {title: "Home", url: "/"},
              {title: "Facilities", url: '/facilities'},
              {title: "Sections", url: '/facilities/:id/sections'},
              {title: "Create", active: true}
            ],
            pageTitle: "Facility Sections",
          }
        },

        {
          path: "/facilities/:id/sections/:sectionId",
          name: "facilities.sections",
          component: () => import("@/views/pages/facilities/sections/Section.vue"),
          meta: {
            breadcrumb: [
              {title: "Home", url: "/"},
              {title: "Facilities", url: '/facilities'},
              {title: "Sections", url: '/facilities/:id/sections'},
            ],
            pageTitle: "Facility Sections",
          },
          children: [{
              path: "/facilities/:id/sections/:sectionId",
              name: "facilities.sections.view",
              component: () => import("@/views/pages/facilities/sections/SectionView.vue"),
              meta: {
                breadcrumb: [
                  {title: "Home", url: "/"},    
                  {title: "Facilities", url: '/facilities'},    
                  {title: "Sections", url: '/facilities/:id/sections'},    
                  {title: "Details", active: true}
                ],
                pageTitle: "Facility Details",
              }
            },
            {
              path: "/facilities/:id/sections/:sectionId/edit",
              name: "facilities.sections.edit",
              component: () => import("@/views/pages/facilities/sections/SectionEdit.vue"),
              meta: {
                breadcrumb: [
                  {title: "Home", url: "/"},    
                  {title: "Facilities", url: '/facilities'},    
                  {title: "Sections", url: '/facilities/:id/sections'},    
                  {title: "Edit", active: true}
                ],
                pageTitle: "Facility Details",
              }
            },
            {
              path: "/facilities/:id/sections/:sectionId/products",
              name: "facilities.sections.products.list",
              component: () => import("@/views/pages/facilities/sections/products/SectionProducts.vue"),
              meta: {
                breadcrumb: [
                  {title: "Home", url: "/"},    
                  {title: "Facilities", url: '/facilities'},    
                  {title: "Sections", url: '/facilities/:id/sections'},    
                  {title: "Products", active: true}
                ],
                pageTitle: "Section Products",
              }
            },
            {
              path: "/facilities/:id/sections/:sectionId/products/create",
              name: "facilities.sections.products.create",
              component: () => import("@/views/pages/facilities/sections/products/SectionProductsCreate.vue"),
              meta: {
                breadcrumb: [
                  {title: "Home", url: "/"},    
                  {title: "Facilities", url: '/facilities'},    
                  {title: "Sections", url: '/facilities/:id/sections'},    
                  {title: "Products", url: '/facilities/:id/sections/:sectionId/products'},    
                  {title: "Create", active: true}
                ],
                pageTitle: "Section Products",
              }
            },
            {
              path: "/facilities/:id/sections/:sectionId/products/:productId",
              name: "facilities.sections.products.edit",
              component: () => import("@/views/pages/facilities/sections/products/SectionProductsEdit.vue"),
              meta: {
                breadcrumb: [
                  {title: "Home", url: "/"},    
                  {title: "Facilities", url: '/facilities'},    
                  {title: "Sections", url: '/facilities/:id/sections'},    
                  {title: "Products", url: '/facilities/:id/sections/:sectionId/products'},    
                  {title: "Edit", active: true}
                ],
                pageTitle: "Section Products",
              }
            },
            {
              path: "/facilities/:id/sections/:sectionId/risk-codes",
              name: "facilities.sections.riskCodes.list",
              component: () => import("@/views/pages/facilities/sections/risk-codes/SectionRiskCodes.vue"),
              meta: {
                breadcrumb: [
                  {title: "Home", url: "/"},    
                  {title: "Facilities", url: '/facilities'},    
                  {title: "Sections", url: '/facilities/:id/sections'},    
                  {title: "Risk Codes", active: true}
                ],
                pageTitle: "Section Lloyds Risk Codes",
              }
            },
            {
              path: "/facilities/:id/sections/:sectionId/risk-codes/create",
              name: "facilities.sections.riskCodes.create",
              component: () => import("@/views/pages/facilities/sections/risk-codes/SectionRiskCodesCreate.vue"),
              meta: {
                breadcrumb: [
                  {title: "Home", url: "/"},    
                  {title: "Facilities", url: '/facilities'},    
                  {title: "Sections", url: '/facilities/:id/sections'},    
                  {title: "Risk Codes", url: '/facilities/:id/sections/:sectionId/risk-codes'},    
                  {title: "Create", active: true}
                ],
                pageTitle: "Section Lloyds Risk Codes",
              }
            },
            {
              path: "/facilities/:id/sections/:sectionId/risk-codes/:riskCodeId",
              name: "facilities.sections.riskCodes.edit",
              component: () => import("@/views/pages/facilities/sections/risk-codes/SectionRiskCodesEdit.vue"),
              meta: {
                breadcrumb: [
                  {title: "Home", url: "/"},    
                  {title: "Facilities", url: '/facilities'},    
                  {title: "Sections", url: '/facilities/:id/sections'},    
                  {title: "Risk Codes", url: '/facilities/:id/sections/:sectionId/risk-codes'},    
                  {title: "Edit", active: true}
                ],
                pageTitle: "Section Lloyds Risk Codes",
              }
            },
            {
              path: "/facilities/:id/sections/:sectionId/ancestors",
              name: "facilities.sections.ancestors.list",
              component: () => import("@/views/pages/facilities/sections/ancestors/SectionAncestors.vue"),
              meta: {
                breadcrumb: [
                  {title: "Home", url: "/"},    
                  {title: "Facilities", url: '/facilities'},    
                  {title: "Sections", url: '/facilities/:id/sections'},    
                  {title: "Ancestors", active: true}
                ],
                pageTitle: "Section Ancestors",
              }
            },
            {
              path: "/facilities/:id/sections/:sectionId/ancestors/create",
              name: "facilities.sections.ancestors.create",
              component: () => import("@/views/pages/facilities/sections/ancestors/SectionAncestorsCreate.vue"),
              meta: {
                breadcrumb: [
                  {title: "Home", url: "/"},    
                  {title: "Facilities", url: '/facilities'},    
                  {title: "Sections", url: '/facilities/:id/sections'},    
                  {title: "Ancestors", url: '/facilities/:id/sections/:sectionId/ancestors'},    
                  {title: "Create", active: true}
                ],
                pageTitle: "Section Ancestors",
              }
            },
            {
              path: "/facilities/:id/sections/:sectionId/ancestors/:ancestorId",
              name: "facilities.sections.ancestors.edit",
              component: () => import("@/views/pages/facilities/sections/ancestors/SectionAncestorsEdit.vue"),
              meta: {
                breadcrumb: [
                  {title: "Home", url: "/"},    
                  {title: "Facilities", url: '/facilities'},    
                  {title: "Sections", url: '/facilities/:id/sections'},    
                  {title: "Ancestors", url: '/facilities/:id/sections/:sectionId/ancestors'},    
                  {title: "Edit", active: true}
                ],
                pageTitle: "Section Ancestors",
              }
            },
          ]
        },
        {
          path: "/facilities/:id/markets",
          name: "facilities.markets.list",
          component: () => import("@/views/pages/facilities/markets/MarketList.vue"),
          meta: {
            breadcrumb: [
              {title: "Home", url: "/"},
              {title: "Facilities", url: '/facilities'},
              {title: "Markets", url: '/facilities', active: true},
            ],
            pageTitle: "Facility Markets",
          }
        },
        {
          path: "/facilities/:id/markets/create",
          name: "facilities.markets.create",
          component: () => import("@/views/pages/facilities/markets/MarketCreate.vue"),
          meta: {
            breadcrumb: [
              {title: "Home", url: "/"},
              {title: "Facilities", url: '/facilities'},
              {title: "Markets", url: '/facilities/:id/markets'},
              {title: "Create", active: true}
            ],
            pageTitle: "Facility Markets",
          }
        },
        {
          path: "/facilities/:id/markets/:marketId",
          name: "facilities.markets.view",
          component: () => import("@/views/pages/facilities/markets/MarketView.vue"),
          meta: {
            breadcrumb: [
              {title: "Home", url: "/"},
              {title: "Facilities", url: '/facilities'},
              {title: "Markets", url: '/facilities/:id/markets'},
              {title: "Details", active: true}
            ],
            pageTitle: "Facility Details",
          }
        },
        {
          path: "/facilities/:id/markets/:marketId/edit",
          name: "facilities.markets.edit",
          component: () => import("@/views/pages/facilities/markets/MarketEdit.vue"),
          meta: {
            breadcrumb: [
              {title: "Home", url: "/"},
              {title: "Facilities", url: '/facilities'},
              {title: "Markets", url: '/facilities/:id/markets'},
              {title: "Details", active: true}
            ],
            pageTitle: "Facility Details",
          }
        },
        {
          path: "/facilities/:id/fdos",
          name: "facilities.fdos.list",
          component: () => import("@/views/pages/facilities/fdos/FDOList.vue"),
          meta: {
            breadcrumb: [
              {title: "Home", url: "/"},
              {title: "Facilities", url: '/facilities'},
              {title: "FDOs", url: '/facilities', active: true},
            ],
            pageTitle: "Facility FDOs",
          }
        },
        {
          path: "/facilities/:id/fdos/create",
          name: "facilities.fdos.create",
          component: () => import("@/views/pages/facilities/fdos/FDOCreate.vue"),
          meta: {
            breadcrumb: [
              {title: "Home", url: "/"},
              {title: "Facilities", url: '/facilities'},
              {title: "FDOs", url: '/facilities/:id/fdos'},
              {title: "Create", active: true}
            ],
            pageTitle: "Facility FDOs",
          }
        },
        {
          path: "/facilities/:id/fdos/:fdoId",
          name: "facilities.fdos.view",
          component: () => import("@/views/pages/facilities/fdos/FDOView.vue"),
          meta: {
            breadcrumb: [
              {title: "Home", url: "/"},
              {title: "Facilities", url: '/facilities'},
              {title: "FDOs", url: '/facilities/:id/fdos'},
              {title: "Details", active: true}
            ],
            pageTitle: "Facility Details",
          }
        },
        {
          path: "/facilities/:id/fdos/:fdoId/edit",
          name: "facilities.fdos.edit",
          component: () => import("@/views/pages/facilities/fdos/FDOEdit.vue"),
          meta: {
            breadcrumb: [
              {title: "Home", url: "/"},
              {title: "Facilities", url: '/facilities'},
              {title: "FDOs", url: '/facilities/:id/fdos'},
              {title: "Edit", active: true}
            ],
            pageTitle: "Facility Details",
          }
        },
        {
          path: "/facilities/:id/distribution-lists",
          name: "facilities.distribution-lists.list",
          component: () => import("@/views/pages/facilities/distribution-lists/DistributionListList.vue"),
          meta: {
            breadcrumb: [
              {title: "Home", url: "/"},
              {title: "Facilities", url: '/facilities'},
              {title: "Distribution Lists", url: '/facilities', active: true},
            ],
            pageTitle: "Facility Distribution Lists",
          }
        },
        {
          path: "/facilities/:id/distribution-lists/create",
          name: "facilities.distribution-lists.create",
          component: () => import("@/views/pages/facilities/distribution-lists/DistributionListCreate.vue"),
          meta: {
            breadcrumb: [
              {title: "Home", url: "/"},
              {title: "Facilities", url: '/facilities'},
              {title: "Distribution Lists", url: '/facilities/:id/distribution-lists/'},
              {title: "Create", active: true}
            ],
            pageTitle: "Facility Distribution Lists",
          },
          props: true,
        },
        {
          path: "/facilities/:id/distribution-lists/:distributionListId",
          name: "facilities.distribution-lists.view",
          component: () => import("@/views/pages/facilities/distribution-lists/DistributionListView.vue"),
          meta: {
            breadcrumb: [
              {title: "Home", url: "/"},
              {title: "Facilities", url: '/facilities'},
              {title: "Distribution Lists", url: '/facilities/:id/distribution-lists/'},
              {title: "View", active: true}
            ],
            pageTitle: "Facility Details",
          }
        },
        {
          path: "/facilities/:id/distribution-lists/:distributionListId/edit",
          name: "facilities.distribution-lists.edit",
          component: () => import("@/views/pages/facilities/distribution-lists/DistributionListEdit.vue"),
          meta: {
            breadcrumb: [
              {title: "Home", url: "/"},
              {title: "Facilities", url: '/facilities'},
              {title: "Distribution Lists", url: '/facilities/:id/distribution-lists/'},
              {title: "Edit", active: true}
            ],
            pageTitle: "Facility Details",
          }
        },
      ],
    },
    {
      path: "/facilities/:id/edit",
      name: "facilities.edit",
      component: () => import("@/views/pages/facilities/FacilityEdit.vue"),
      meta: {
        breadcrumb: [
          {title: "Home", url: "/"},
          {title: "Facilities", url: '/facilities'},
          {title: "Details", url: '/facilities/:id/'},
          {title: "Edit", active: true}
        ],
        pageTitle: "Facility Details",
      }
    },
    {
      path: "/product-documents/wording",
      name: "wording.list",
      component: () => import("@/views/pages/product-documents/wording/WordingList.vue"),
      meta: {
        breadcrumb: [
          {title: "Home", url: "/"},
          {title: "Product Documents"},
          {title: "Wording", url: '/product-documents/wording'},
          {title: "List", active: true}
        ],
        pageTitle: "Wording List",
      }
    },
    {
      path: "/product-documents/wording/create",
      name: "wording.create",
      component: () => import("@/views/pages/product-documents/wording/WordingCreate.vue"),
      meta: {
        breadcrumb: [
          {title: "Home", url: "/"},
          {title: "Product Documents"},
          {title: "Wording", url: '/product-documents/wording'},
          {title: "Create", active: true}
        ],
        pageTitle: "Upload Wording Document",
      }
    },
    {
      path: "/product-documents/wording/:id/edit",
      name: "wording.edit",
      component: () => import("@/views/pages/product-documents/wording/WordingEdit.vue"),
      meta: {
        breadcrumb: [
          {title: "Home", url: "/"},
          {title: "Product Documents"},
          {title: "Wording", url: '/product-documents/wording'},
          {title: "Edit", active: true}
        ],
        pageTitle: "Edit Wording Document",
      }
    },
    {
      path: "/product-documents/standard-clause",
      name: "standard-clause.list",
      component: () => import("@/views/pages/product-documents/standard-clause/StandardClauseList.vue"),
      meta: {
        breadcrumb: [
          {title: "Home", url: "/"},
          {title: "Product Documents"},
          {title: "Standard Clause", url: '/product-documents/standard-clause'},
          {title: "List", active: true}
        ],
        pageTitle: "Standard Clause List",
      }
    },
    {
      path: "/product-documents/standard-clause/create",
      name: "standard-clause.create",
      component: () => import("@/views/pages/product-documents/standard-clause/StandardClauseCreate.vue"),
      meta: {
        breadcrumb: [
          {title: "Home", url: "/"},
          {title: "Product Documents"},
          {title: "Standard Clause", url: '/product-documents/standard-clause'},
          {title: "Create", active: true}
        ],
        pageTitle: "Upload Standard Clause Document",
      }
    },
    {
      path: "/product-documents/standard-clause/:id/edit",
      name: "standard-clause.edit",
      component: () => import("@/views/pages/product-documents/standard-clause/StandardClauseEdit.vue"),
      meta: {
        breadcrumb: [
          {title: "Home", url: "/"},
          {title: "Product Documents"},
          {title: "Standard Clause", url: '/product-documents/standard-clause'},
          {title: "Edit", active: true}
        ],
        pageTitle: "Edit Standard Clause Document",
      }
    },
    {
      path: "/product-documents/front-jacket",
      name: "front-jacket.list",
      component: () => import("@/views/pages/product-documents/front-jacket/FrontJacketList.vue"),
      meta: {
        breadcrumb: [
          {title: "Home", url: "/"},
          {title: "Product Documents"},
          {title: "Front Jacket", url: '/product-documents/front-jacket'},
          {title: "List", active: true}
        ],
        pageTitle: "Front Jacket List",
      }
    },
    {
      path: "/product-documents/front-jacket/create",
      name: "front-jacket.create",
      component: () => import("@/views/pages/product-documents/front-jacket/FrontJacketCreate.vue"),
      meta: {
        breadcrumb: [
          {title: "Home", url: "/"},
          {title: "Product Documents"},
          {title: "Front Jacket", url: '/product-documents/front-jacket'},
          {title: "Create", active: true}
        ],
        pageTitle: "Upload Front Jacket Document",
      }
    },
    {
      path: "/product-documents/front-jacket/:id/edit",
      name: "front-jacket.edit",
      component: () => import("@/views/pages/product-documents/front-jacket/FrontJacketEdit.vue"),
      meta: {
        breadcrumb: [
          {title: "Home", url: "/"},
          {title: "Product Documents"},
          {title: "Front Jacket", url: '/product-documents/front-jacket'},
          {title: "Edit", active: true}
        ],
        pageTitle: "Edit Front Jacket Document",
      }
    },
    {
      path: "/product-documents/rear-jacket",
      name: "rear-jacket.list",
      component: () => import("@/views/pages/product-documents/rear-jacket/RearJacketList.vue"),
      meta: {
        breadcrumb: [
          {title: "Home", url: "/"},
          {title: "Product Documents"},
          {title: "Rear Jacket", url: '/product-documents/rear-jacket'},
          {title: "List", active: true}
        ],
        pageTitle: "Rear Jacket List",
      }
    },
    {
      path: "/product-documents/rear-jacket/create",
      name: "rear-jacket.create",
      component: () => import("@/views/pages/product-documents/rear-jacket/RearJacketCreate.vue"),
      meta: {
        breadcrumb: [
          {title: "Home", url: "/"},
          {title: "Product Documents"},
          {title: "Rear Jacket", url: '/product-documents/rear-jacket'},
          {title: "Create", active: true}
        ],
        pageTitle: "Upload Rear Jacket Document",
      }
    },
    {
      path: "/product-documents/rear-jacket/:id/edit",
      name: "rear-jacket.edit",
      component: () => import("@/views/pages/product-documents/rear-jacket/RearJacketEdit.vue"),
      meta: {
        breadcrumb: [
          {title: "Home", url: "/"},
          {title: "Product Documents"},
          {title: "Rear Jacket", url: '/product-documents/rear-jacket'},
          {title: "Edit", active: true}
        ],
        pageTitle: "Edit Rear Jacket Document",
      }
    },
    {
      path: "/product-documents/:typeId",
      name: "dynamic.list",
      component: () => import("@/views/pages/product-documents/dynamic/DynamicList.vue"),
      meta: {
        breadcrumb: [
          {title: "Home", url: "/"},
          {title: "Product Documents", url: "REMOVEME"},
          {title: "Dynamic", url: '/product-documents/:typeId', dynamic: true},
          {title: "List", active: true}
        ],
        pageTitle: "Dynamic List",
      }
    },
    {
      path: "/product-documents/:typeId/create",
      name: "dynamic.create",
      component: () => import("@/views/pages/product-documents/dynamic/DynamicCreate.vue"),
      meta: {
        breadcrumb: [
          {title: "Home", url: "/"},
          {title: "Product Documents", url: "REMOVEME"},
          {title: "Dynamic", url: '/product-documents/:typeId', dynamic: true},
          {title: "Create", active: true}
        ],
        pageTitle: "Upload Dynamic Document",
      }
    },
    {
      path: "/product-documents/:typeId/:id/edit",
      name: "dynamic.edit",
      component: () => import("@/views/pages/product-documents/dynamic/DynamicEdit.vue"),
      meta: {
        breadcrumb: [
          {title: "Home", url: "/"},
          {title: "Product Documents", url: "REMOVEME"},
          {title: "Dynamic", url: '/product-documents/:typeId', dynamic: true},
          {title: "Edit", active: true}
        ],
        pageTitle: "Edit Dynamic Document",
      }
    },
  ]
}, ];

if (process.env.VUE_APP_ENVIRONMENT === 'development') {
  const debug = {
    path: "/debug",
    name: "debug",
    component: () => import("@/views/pages/debug/Debug.vue"),
    meta: {
      breadcrumb: [
        {title: "Home", url: "/"},
        {title: "Debug", url: "/debug", active: true},
      ],
      pageTitle: "Debug",
    }
  }

  authRoutes[0].children.push(debug);
}

// IPR routes removed.

export default ({
  authGuard,
  guestGuard
}) => [
  ...authGuard(authRoutes),
  // Guest routes.
  ...guestGuard([{
      path: "*",
      redirect: {
        name: "login"
      }
    },
    {
      path: "/login",
      name: "login",
      component: () => import("@/views/Callback.vue"),
      meta: {
        rule: "editor"
      }
    },
    {
      path: "/callback",
      name: "auth-callback",
      component: () => import("@/views/Callback.vue"),
      meta: {
        rule: "editor"
      }
    },
  ])
]
